<template>
    <div v-loading="isLoading" class="voucher-card" :class="{ editing: isEditing, canceled: form.cnl }">
        <el-form ref="form" :model="form" :rules="rules" label-position="left">
            <div class="card-body">
                <div class="voucher-head">
                    <template v-if="!isEditing && form.includeInReport === false">
                        <el-tooltip>
                            <template slot="content">
                                <div style="max-width: 250px; line-height: 150%;">
                                    <div>此傳票在報表鎖定後產生，所以未列入該年度會計報表。</div>
                                    <div style="font-size: 9.5pt; font-weight: normal; margin-top: 12px;">
                                        請至「<span @click="$router.push({name: 'accounting-yearly-management'})" style="text-decoration: underline; cursor: pointer;">年度會計總管理</span>」處理此傳票
                                    </div>
                                </div>
                            </template>
                            <span class="not-included-label">未列入報表</span>
                        </el-tooltip>
                    </template>
                    <div>
                        <div class="company-name">{{ companyName }}<span v-if="isTrying">（試用）</span></div>
                        <div class="voucher-name">
                            <span>{{ voucherTypeTxt }}傳票</span>
                            <span class="canceled-label" v-if="form.cnl">（已作廢）</span>
                        </div>
                        <div class="date-display">
                            <span v-if="!isEditing">{{ formatDatInChinese(form.dat) }}</span>
                        </div>
                    </div>
                    <div class="infos">
                        <div class="left">
                            <div v-if="form.sno">傳票狀態：
                                <span v-if="form.status === 0">待辦</span>
                                <span v-if="form.status === 1"><i class="el-icon-check"></i> 已核准</span>
                            </div>
                            <div class="date">
                                <span v-if="isEditing">
                                    <el-form-item prop="dat" label="傳票日期：" style="display: flex">
                                        <el-date-picker
                                            v-model="form.dat"
                                            :clearable="false"
                                            :disabled="voucherData.transactionType === 13"
                                            value-format="timestamp"
                                            style="width: 140px"
                                            type="date"
                                            placeholder="選擇日期"
                                            size="small"
                                        />
                                    </el-form-item>
                                </span>
                            </div>
                        </div>
                        <div class="right">
                            <div v-if="form.sno">
                                <span>傳票編號：{{ form.serialNumber }}</span>
                                <span v-if="form.isSystem">(系統)</span>
                                <span v-else>(自編)</span>
                            </div>
                        </div>
                    </div>
                </div>
                <table class="voucher-table" cellspacing="0" cellpadding="0">
                    <thead>
                        <tr>
                            <th class="setting">會計科目</th>
                            <th class="content">摘要</th>
                            <th class="crd-amt">借方金額</th>
                            <th class="deb-amt">貸方金額</th>
                            <th class="del" v-if="isEditing">刪除</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in form.accountItems" :key="item.sno">
                            <td class="setting">
                                <div v-if="!isEditing">{{ item.accno }} - {{ item.accnonam }}</div>
                                <ah-select-accounting-setting
                                    v-if="isEditing"
                                    v-model="item.accno"
                                    :ref="`row_${index}_input_acitem`"
                                    size="small"
                                    @change="
                                        (setting) => {
                                            item.accnO_SNO = setting.sno;
                                            onInputEnter(index, 'acitem');
                                        }
                                    "
                                    @keyPressEnter="onInputEnter(index, 'acitem')"
                                />
                            </td>
                            <td class="content">
                                <div v-if="!isEditing">
                                    {{ item.content }}
                                </div>
                                <el-input v-if="isEditing" v-model="item.content" size="small" :ref="`row_${index}_input_content`" @keypress.enter.native="onInputEnter(index, 'content')" />
                            </td>
                            <td class="crd-amt">
                                <Money v-if="!isEditing && item.crD_NTD" :v="item.crD_NTD" />
                                <money-input v-if="isEditing" v-model="item.crD_NTD" size="small" :ref="`row_${index}_input_crd`" @keypress.enter.native="onInputEnter(index, 'crd')" />
                            </td>
                            <td class="deb-amt">
                                <Money v-if="!isEditing && item.deB_NTD" :v="item.deB_NTD" />
                                <money-input v-model="item.deB_NTD" v-if="isEditing" size="small" :ref="`row_${index}_input_deb`" @keypress.enter.native="onInputEnter(index, 'deb')" />
                            </td>
                            <td class="del" v-if="isEditing">
                                <el-button size="mini" icon="el-icon-delete" @click="deleteVoucherItem(item)"></el-button>
                            </td>
                        </tr>
                        <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td v-if="isEditing"></td>
                        </tr>
                        <tr v-if="isEditing" class="editing-row">
                            <td><el-button size="mini" icon="el-icon-plus" @click="addVoucherItem()">增加項目</el-button></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                        <tr class="total-row">
                            <td colspan="2" class="content sum-label-cell">合計</td>
                            <td class="crd-amt"><Money :v="getVoucherItemSum(form, 'crd')" :class="{ warning: !isDebitCreditBalance }" /></td>
                            <td class="deb-amt"><Money :v="getVoucherItemSum(form, 'deb')" :class="{ warning: !isDebitCreditBalance }" /></td>
                            <td v-if="isEditing"></td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="form.sno" class="credits">
                    <div>
                        <div>編輯人：{{ form.upD_USR }} ({{ form.upD_DAT | dateFormater("YYYY-MM-dd") }})</div>
                        <div>
                            <span>歸入結算日期：</span>
                            <span v-if="form.settlementBelongDate">{{ form.settlementBelongDate | dateFormater("YYYY-MM-dd") }}</span>
                            <span v-else> --- </span>
                        </div>
                        <div v-if="isSystemNoShow">
                            <span>Sokuji系統單號：</span>
                            <el-button v-if="voucherData.transactionType < 7 || voucherData.transactionType === 9" type="text" style="padding: 0" @click="onOpenDetail">
                                {{ voucherData.unT_NBR }}
                            </el-button>
                            <span v-else>{{ voucherData.unT_NBR }}</span>
                        </div>
                    </div>
                    <div>
                        {{ form.statuS_UPD && form.status === 0 ? "取消核准人" : "核准人" }}：{{ form.statuS_UPD || "---" }} (
                        <span v-if="form.statuS_DAT">{{ form.statuS_DAT | dateFormater("YYYY-MM-dd") }}</span>
                        <span v-else> --- </span>
                        )
                    </div>
                </div>
                <div v-if="showSignature" class="signature-field">
                    <span>簽章：</span><div class="signature-line"></div>
                </div>
            </div>
        </el-form>
        <div v-if="isAuthOne(['G06', 'G09']) && editable && voucherData.vocherStatus !== 1" class="card-footer">
            <div v-if="!isEditing" class="normal-actions">
                <div class="approve-actions">
                    <template v-if="form.isSystem">
                        <el-button
                            v-if="isAuth('G06') && !addEmptyVoucherMode && form.status !== 1 && !form.cnl"
                            :disabled="!isPayed('G01') && !isAuth('admin')"
                            @click="onVoid"
                            icon="el-icon-document-delete"
                            size="small"
                            >作廢</el-button
                        >
                        <el-button
                            v-if="isAuth('G06') && !addEmptyVoucherMode && form.status !== 1 && form.cnl"
                            :disabled="!isPayed('G01') && !isAuth('admin')"
                            @click="onCancelVoid"
                            icon="el-icon-document"
                            size="small"
                            >取消作廢</el-button
                        >
                    </template>
                    <el-button v-else-if="isAccountsVoucherMutable" :disabled="(!isPayed('G01') && !isAuth('admin')) || form.isSystem" @click="onDelete" icon="el-icon-delete" size="small"
                        >刪除</el-button
                    >
                    <el-button
                        v-if="!addEmptyVoucherMode"
                        :disabled="(!isPayed('G01') && !isAuth('admin')) || isOtherVoucherEditing"
                        @click="duplicateEdit"
                        icon="el-icon-document-copy"
                        size="small"
                        >複製</el-button
                    >
                    <el-button
                        v-if="isAccountsVoucherMutable && !form.cnl"
                        :disabled="(!isPayed('G01') && !isAuth('admin')) || isOtherVoucherEditing"
                        @click="activeEdit"
                        icon="el-icon-edit"
                        size="small"
                        >編輯</el-button
                    >
                    <el-tooltip :content="hasEmptyAccountingItem ? '尚有會計科目未選擇，無法核准。' : '無相關權限。'" :manual="!hasEmptyAccountingItem" placement="top" effect="dark">
                        <el-button
                            v-if="isAuth('G09') && !addEmptyVoucherMode && form.status !== 1 && !form.cnl"
                            :disabled="(!isPayed('G01') && !isAuth('admin')) || hasEmptyAccountingItem"
                            @click="onApprove"
                            type="success"
                            icon="el-icon-check"
                            size="small"
                            style="width: 120px"
                            >核准</el-button
                        >
                    </el-tooltip>
                    <el-button
                        v-if="isAuth('G09') && !addEmptyVoucherMode && form.status === 1 && !form.cnl"
                        :disabled="!isPayed('G01') && !isAuth('admin')"
                        @click="onCancelApprove"
                        icon="el-icon-refresh-left"
                        size="small"
                        >取消核准</el-button
                    >
                </div>
            </div>
            <div v-else class="editing-actions">
                <el-button type="" @click="cancelEdit" icon="el-icon-close" size="small">取消</el-button>
                <el-tooltip content="借貸不平衡，請檢查傳票項目。" :manual="isDebitCreditBalance" placement="top" effect="dark">
                    <el-button type="primary" :disabled="(!isPayed('G01') && !isAuth('admin')) || !isDebitCreditBalance" icon="el-icon-check" size="small" style="width: 120px" @click="submitEdit"
                        >儲存</el-button
                    >
                </el-tooltip>
            </div>
        </div>
        <InvoiceDetailDialog v-if="voucherData.transactionType === 1" @close="handleInvoiceDetailDialogClose" :visible="invoiceDetailDialog.visible" :sno="invoiceDetailDialog.sno" />
        <PaymentDetailDialog v-if="voucherData.transactionType === 2" @close="handlePaymentDetailDialogClose" :visible="paymentDetailDialog.visible" :sno="paymentDetailDialog.sno" />
        <VariableDetailDialog
            v-if="voucherData.transactionType >= 3 && voucherData.transactionType <= 6"
            @close="handleVariableDetailDialogClose"
            :visible="variableDetailDialog.visible"
            :sno="variableDetailDialog.sno"
        />
        <BankTransferDetailDialog :visible="bankTransferDetailDialog.visible" :sno="bankTransferDetailDialog.sno" @close="handleBankTransferDetailDialogClose" />
        <!-- <PettyCashDetailDialog :visible="pettyCashDetailDialog.visible" :data="pettyCashDetailDialog.data" @close="handlePettyCashDetailDialogClose" /> -->
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import MoneyInput from '@/components/ui/MoneyInput.vue';
import ahSelectAccountingSetting from '@/components/ui/ahSelectAccountingSetting.vue';

export default {
    name: 'VoucherCard',
    components: {
        MoneyInput,
        ahSelectAccountingSetting,

        InvoiceDetailDialog: () => import('@/components/dashboard/Invoice/InvoiceDetailDialog.vue'),
        PaymentDetailDialog: () => import('@/components/dashboard/Payment/PaymentDetailDialog.vue'),
        VariableDetailDialog: () => import('@/components/dashboard/Finance/VariableStatement/DetailDialog.vue'),
        BankTransferDetailDialog: () => import('@/components/dashboard/Finance/VariableStatement/BankTransferDetailDialog.vue'),
        // PettyCashDetailDialog: () => import('@/components/dashboard/Finance/PettyCash/DetailDialog.vue'),
    },
    props: {
        editable: {
            type: Boolean,
            default: false,
        },
        queryType: {
            type: Number,
            default: null,
        },
        UNT_NBR: {
            type: String,
            default: null,
        },
        accountTypeSno: {
            type: [Number, Object],
            default: null,
        },
        voucherData: {
            type: Object,
            default: () => ({
                accountItems: [],
                dat: null,
                cnl: false,
                cny: 'TWD',
                mem: null,
                sno: null,
                unT_NBR: null,
                unt: null,
                upD_USR: null,
                vono: null,
                voucherType: null,
                isEditing: false,
                includeInReport: null,
            }),
        },
        addEmptyVoucherMode: {
            type: Boolean,
            default: false,
        },
        isDialog: {
            type: Boolean,
            default: false,
        },
        isOpen: Boolean,
        openYear: Number,
        titleName: String,
        isOtherVoucherEditing: Boolean,
        showSignature: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isEditing: false,
            isLoading: false,
            itemsToDelete: [],
            lastInputFocusTime: null,
            form: {
                accountItems: [],
                dat: null,
                cnl: false,
                cny: 'TWD',
                mem: null,
                sno: null,
                unT_NBR: null,
                unt: null,
                upD_USR: null,
                vono: null,
                voucherType: null,
            },
            rules: {
                dat: [{ required: true, message: '請輸入傳票日期' }],
            },

            invoiceDetailDialog: {
                visible: false,
                sno: null,
            },
            paymentDetailDialog: {
                visible: false,
                sno: null,
            },
            variableDetailDialog: {
                visible: false,
                sno: null,
            },
            bankTransferDetailDialog: {
                visible: false,
                sno: null,
            },
            pettyCashDetailDialog: {
                visible: false,
                sno: null,
            },
        };
    },
    computed: {
        ...mapGetters({ isTrying: 'menu/isTrying' }),
        companyName() {
            return this.$store.getters['menu/companyInfo'].cH_CMP;
        },
        voucherTypeTxt() {
            switch (this.voucherData.transactionType) {
            case 12:
                return '開帳';
            case 13:
                return '結帳';
            case 15:
                return '調整';
            default:
                return '轉帳';
            }
        },
        isAccountsVoucherMutable() {
            return (
                this.isOpenVoucherEditable
                || (this.voucherData.transactionType !== 12 && this.voucherData.transactionType !== 13 && this.isAuth('G06') && !this.addEmptyVoucherMode && this.form.status !== 1)
            );
        },
        isOpenVoucherEditable() {
            return (this.voucherData.transactionType === 12 || this.voucherData.transactionType === 13) && this.isAuth('G12');
        },
        isDebitCreditBalance() {
            return _.sumBy(this.form.accountItems, 'crD_NTD') === _.sumBy(this.form.accountItems, 'deB_NTD');
        },
        isSystemNoShow() {
            return (
                this.voucherData.unT_NBR
                && this.voucherData.transactionType !== 10
                && this.voucherData.transactionType !== 7
                && this.voucherData.unt !== 'YEAR'
                && this.voucherData.unt !== 'ModifyVohcer'
                && this.voucherData.unt !== 'IncomeTax'
            );
        },
        voidAccountTypeSno() {
            switch (this.queryType) {
            case 7: // 薪資支出
                return undefined;
            case 8:
                if (this.accountTypeSno.repaymentSno) return this.accountTypeSno.repaymentSno;
                return this.accountTypeSno.drawdownSno;

            default:
                return this.accountTypeSno;
            }
        },
        hasEmptyAccountingItem() {
            return !!_.find(this.form.accountItems, ['accno', null]);
        },
    },
    mounted() {
        // 將 prop 傳入的 voucherData 複製到 form 用於表單編輯
        this.updateFormData();

        // 切換編輯狀態
        if (this.voucherData.isEditing === true) {
            this.isEditing = true;
        } else {
            this.isEditing = false;
        }
    },
    methods: {
        updateFormData() {
            this.form = JSON.parse(JSON.stringify(this.voucherData));
            if (this.isOpen) this.form.unt = 'YEAR';
        },

        /**
         * 民國年轉換
         */
        formatDatInChinese(dat) {
            const DATE = moment(dat).format('DD');
            const MONTH = moment(dat).format('MM');
            const YEAR = moment(dat).year() - 1911;
            return `民國 ${YEAR} 年 ${MONTH} 月 ${DATE} 日`;
        },

        /**
         * 編輯
         */
        activeEdit() {
            this.isEditing = true;
            this.$emit('edit', this.form);
        },

        /**
         * 取消編輯
         */
        cancelEdit() {
            this.isEditing = false;
            this.updateFormData();
            this.$emit('cancel', this.form);
        },

        /**
         * 新增傳票項目（空白）
         */
        addVoucherItem() {
            const newItem = {
                vocherSno: this.voucherData.sno,
                vono: this.voucherData.vono,
                accnO_SNO: null,
                content: this.titleName || null,
                crD_NTD: 0,
                deB_NTD: 0,
            };
            this.form.accountItems.push(newItem);
        },

        /**
         * 批次新增傳票項目 for 編輯
         */
        addVoucherItems(items) {
            items.forEach((item) => {
                setTimeout(() => {
                    this.form.accountItems.push({
                        vocherSno: this.form.sno,
                        vono: this.form.vono,
                        accnO_SNO: item.accnO_SNO,
                        accno: item.accno,
                        accnonam: item.accnonam,
                        content: item.content,
                        crD_NTD: item.crD_NTD,
                        deB_NTD: item.deB_NTD,
                    });
                }, 10);
            });
        },

        /**
         * 移除傳票項目
         */
        deleteVoucherItem(item) {
            // 從顯示清單中移除
            const index = this.form.accountItems.indexOf(item);
            if (index > -1) {
                this.form.accountItems.splice(index, 1);
            }

            // 記下來讓api刪除（傳票已建立後）
            if (this.form.sno) {
                if (item.sno !== null) {
                    this.itemsToDelete.push(item.sno);
                }
            }
        },

        /**
         * 傳票項目加總
         */
        getVoucherItemSum(voucher, type) {
            if (type === 'crd') return _.sumBy(voucher.accountItems, 'crD_NTD');
            if (type === 'deb') return _.sumBy(voucher.accountItems, 'deB_NTD');
            return 0;
        },

        /**
         * 更新傳票資訊，使用api取得最新資訊後，覆蓋此元件的 form 參數
         */
        async updateData() {
            if (this.isDialog) return;

            this.isLoading = true;
            try {
                const resp = await this.$api({
                    url: '/AccountingSummaryPage/GetVouchers',
                    params: {
                        UNT_NBR: this.form.unT_NBR,
                    },
                    method: 'GET',
                });

                const vouchers = resp.data.items;
                const newVoucherData = vouchers.find((v) => v.sno === this.form.sno);
                if (newVoucherData) {
                    this.form = JSON.parse(JSON.stringify(newVoucherData));
                }
                this.itemsToDelete = [];
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * 儲存傳票資訊
         */
        submitEdit() {
            this.$refs.form.validate(async (valid) => {
                if (valid) {
                    if (this.hasEmptyAccountingItem) {
                        this.$message({ showClose: true, message: '尚有會計科目未填寫', type: 'error' });
                        return;
                    }
                    this.isEditing = false;
                    this.isLoading = true;

                    try {
                        // 儲存變更
                        const resp = await this.$api({
                            url: '/AccountingSummaryPage/UpsertVouchers',
                            method: 'POST',
                            data: this.form,
                        });
                        await this.checkIsCheckout();
                        this.form = resp.data.item;

                        this.$message({ showClose: true, message: '傳票已儲存', type: 'success' });
                    } catch (ex) {
                        this.$message({ showClose: true, message: '傳票無法儲存', type: 'error' });
                    } finally {
                        this.isLoading = false;
                    }

                    await this.updateData();

                    // 如果新增自編傳票，需要顯示各會計科目的名稱
                    if (this.addEmptyVoucherMode) {
                        const allOption = _.flatMap(this.$store.getters['accounting/settingsForSelect'], 'children');
                        const newAccountItems = [];
                        this.form.accountItems.forEach((item) => {
                            const copyItem = item;
                            const FOUND = _.find(allOption, ['sno', item.accnO_SNO]);
                            copyItem.accnonam = FOUND.accnam;
                            newAccountItems.push(copyItem);
                        });
                        this.form = JSON.parse(JSON.stringify(this.form));
                    }
                    this.$emit('submit', this.form);
                }
            });
        },

        /**
         * 作廢傳票
         */
        async onVoid() {
            const confirmRes = await this.$confirm('確定要作廢傳票？', {
                confirmButtonText: '確定',
                cancelButtonText: '取消',
            }).catch(() => {});

            if (confirmRes !== 'confirm') return;

            this.isLoading = true;

            try {
                await this.$api({
                    url: '/AccountingSummaryPage/VoidVoucher',
                    method: 'POST',
                    data: {
                        queryType: this.queryType,
                        accountTypeSno: this.voidAccountTypeSno,
                        voucherSno: this.form.sno,
                        UNT_NBR: this.queryType === 7 ? this.UNT_NBR : undefined,
                    },
                });
                this.$message({ showClose: true, message: '傳票已標示為作廢', type: 'success' });
            } catch (ex) {
                this.$message({ showClose: true, message: '傳票無法作廢', type: 'error' });
            } finally {
                this.isLoading = false;
            }

            this.isEditing = false;

            await this.updateData();
            this.$emit('void', this.form);
        },

        /**
         * 取消作廢傳票
         */
        async onCancelVoid() {
            const confirmRes = await this.$confirm('確定要取消作廢傳票？', {
                confirmButtonText: '確定',
                cancelButtonText: '取消',
            }).catch(() => {});

            if (confirmRes !== 'confirm') return;

            this.isLoading = true;

            try {
                await this.$api({
                    url: '/AccountingSummaryPage/CancelVoidVoucher',
                    method: 'POST',
                    data: {
                        queryType: this.queryType,
                        accountTypeSno: this.voidAccountTypeSno,
                        voucherSno: this.form.sno,
                        UNT_NBR: this.queryType === 7 ? this.UNT_NBR : undefined,
                    },
                });
                this.$message({ showClose: true, message: '已取消作廢', type: 'success' });
            } catch (ex) {
                this.$message({ showClose: true, message: '無法取消作廢', type: 'error' });
            } finally {
                this.isLoading = false;
            }

            this.isEditing = false;

            await this.updateData();
            this.$emit('cancelVoid', this.form);
        },

        /**
         * 刪除此傳票
         */
        async onDelete() {
            const confirmRes = await this.$confirm('確定要刪除此傳票嗎？（此動作將無法復原）', {
                confirmButtonText: '確定',
                cancelButtonText: '取消',
            }).catch(() => {});

            if (confirmRes !== 'confirm') return;

            this.isLoading = true;

            try {
                await this.$api({
                    url: '/AccountingSummaryPage/DeleteVoucher',
                    method: 'POST',
                    data: { sno: this.form.sno },
                });
                this.$message({ showClose: true, message: '傳票已刪除', type: 'success' });
            } catch (ex) {
                this.$message({ showClose: true, message: '無法刪除傳票', type: 'error' });
            } finally {
                this.isLoading = false;
            }

            this.$emit('delete', this.form);
        },

        /**
         * 核准
         */
        async onApprove() {
            try {
                await this.$api({
                    url: '/AccountingSummaryPage/UpdateVoucherStatus',
                    method: 'POST',
                    data: [
                        {
                            sno: this.form.sno,
                            status: 1,
                        },
                    ],
                });
                this.$message({ showClose: true, message: '傳票已核准', type: 'success' });
            } catch (ex) {
                this.$message({ showClose: true, message: '無法核准傳票', type: 'error' });
            }
            await this.updateData();
            this.$emit('approve', this.form);
        },

        /**
         * 取消核准
         */
        async onCancelApprove() {
            try {
                await this.$api({
                    url: '/AccountingSummaryPage/UpdateVoucherStatus',
                    method: 'POST',
                    data: [
                        {
                            sno: this.form.sno,
                            status: 0,
                        },
                    ],
                });
                this.$message({ showClose: true, message: '已取消核准', type: 'success' });
            } catch (ex) {
                this.$message({ showClose: true, message: '無法取消核准', type: 'error' });
            }
            await this.updateData();
            this.$emit('cancelApprove', this.form);
        },

        // 檢查是否結帳
        async checkIsCheckout() {
            const DAT = moment(this.form.dat);
            const YEAR = DAT.year() - 1911;

            // 檢查不再提醒標記是否過期
            const NO_HINT_DAY = localStorage.getItem('voucher-no-hint-day');
            if (NO_HINT_DAY !== moment().format('YYYY-MM-DD')) {
                localStorage.removeItem('voucher-no-hint-day');
                localStorage.removeItem('voucher-no-hint-years');
            }

            // 檢查本年度是否提醒
            const NO_HINT_YEARS = JSON.parse(localStorage.getItem('voucher-no-hint-years')) || [];
            if (NO_HINT_YEARS.includes(YEAR)) return;

            // 檢查本年度是否已結算
            const resp = await this.$api({
                url: 'AccountingSummaryPage/QueryVouchers',
                method: 'GET',
                params: {
                    StartDateStart: DAT.startOf('year').format('x'),
                    StartDateEnd: DAT.endOf('year').format('x'),
                    TransactionType: 13,
                },
            });

            if (resp.data.totalNum > 0) {
                try {
                    await this.$confirm(`${YEAR}年度已結算，請重新結算本年度。`, '提醒', {
                        confirmButtonText: '我瞭解了，下次再提醒我',
                        cancelButtonText: '我瞭解了，本年度今天不再提醒',
                        type: 'warning',
                        center: true,
                    });
                } catch (e) {
                    localStorage.setItem('voucher-no-hint-day', moment().format('YYYY-MM-DD'));
                    NO_HINT_YEARS.push(YEAR);
                    localStorage.setItem('voucher-no-hint-years', JSON.stringify(_.uniq(NO_HINT_YEARS)));
                }
            }
        },

        /*
         * 複製傳票
         */
        duplicateEdit() {
            this.$emit('duplicate', this.form);
        },

        onOpenDetail() {
            switch (this.voucherData.transactionType) {
            case 1: // Invoice
                this.openInvoiceDetail(this.voucherData.unT_NBR_SNO);
                break;
            case 2: // Payment
                this.openPaymentDetail(this.voucherData.unT_NBR_SNO);
                break;
            case 3:
            case 4:
            case 5:
            case 6:
                this.openVariableDetail(this.voucherData.unT_NBR_SNO);
                break;
            case 9:
                this.openBankTransferDetail(this.voucherData.unT_NBR_SNO);
                break;
                // case 10:
                //     this.openPettyCashDetail(this.voucherData.unT_NBR_SNO);
                //     break;
            default:
                break;
            }
        },
        openInvoiceDetail(sno) {
            this.invoiceDetailDialog.sno = sno;
            this.invoiceDetailDialog.visible = true;
        },
        handleInvoiceDetailDialogClose(refresh) {
            this.$emit('detailClose', refresh);
            this.invoiceDetailDialog.visible = false;
            this.invoiceDetailDialog.sno = null;
        },
        openPaymentDetail(sno) {
            this.paymentDetailDialog.sno = sno;
            this.paymentDetailDialog.visible = true;
        },
        handlePaymentDetailDialogClose(refresh) {
            this.$emit('detailClose', refresh);
            this.paymentDetailDialog.visible = false;
            this.paymentDetailDialog.sno = null;
        },
        openVariableDetail(sno) {
            this.variableDetailDialog.sno = sno;
            this.variableDetailDialog.visible = true;
        },
        handleVariableDetailDialogClose(refresh) {
            this.$emit('detailClose', refresh);
            this.variableDetailDialog.visible = false;
            this.variableDetailDialog.sno = null;
        },
        openBankTransferDetail(sno) {
            this.bankTransferDetailDialog.sno = sno;
            this.bankTransferDetailDialog.visible = true;
        },
        handleBankTransferDetailDialogClose() {
            this.bankTransferDetailDialog.visible = false;
            this.bankTransferDetailDialog.sno = null;
        },
        // openPettyCashDetail(data) {
        //     this.pettyCashDetailDialog.data = data;
        //     this.pettyCashDetailDialog.visible = true;
        // },
        // handlePettyCashDetailDialogClose(refresh) {
        //     this.$emit('detailClose', refresh);
        //     this.pettyCashDetailDialog.visible = false;
        //     this.pettyCashDetailDialog.sno = null;
        // },
        /**
         * 輸入框按下 Enter
         */
        onInputEnter(rowIndex, fieldName) {
            // 防止會計科目選單 enter 後發送兩次（用時間差判斷）
            if (this.lastInputFocusTime) {
                const currTime = moment();
                const diffTime = currTime.diff(this.lastInputFocusTime);
                // console.log('diffTime', diffTime);
                if (diffTime <= 100) {
                    return;
                }
            }

            const fieldOrder = ['acitem', 'content', 'crd', 'deb'];
            const currFieldIndex = fieldOrder.indexOf(fieldName);
            // console.log('rowIndex', rowIndex);
            // console.log('fieldName', fieldName);

            // 當前欄位無法找到
            if (currFieldIndex === -1 || currFieldIndex >= fieldOrder.length) {
                console.error(`當前輸入欄位無法找到：${fieldName}`);
                return;
            }

            // 取得下一個欄位名稱
            let nextField = fieldOrder[currFieldIndex + 1];
            let nextRowIndex = rowIndex;

            // 如果是最後一個欄位
            if (currFieldIndex === fieldOrder.length - 1) {
                console.log('last field:', nextRowIndex);
                // 已是最後一行，新增一行
                if (rowIndex === this.form.accountItems.length - 1) {
                    // 新增一行
                    this.addVoucherItem();

                    // focus
                    this.$nextTick(() => {
                        this.onInputEnter(rowIndex, fieldName);
                    });
                    return;
                }
                // 非最後一行，移動到下一行第一個欄位
                nextField = fieldOrder.at(0);
                nextRowIndex += 1;
            }

            const nextRef = `row_${nextRowIndex}_input_${nextField}`;
            const nextInput = this.$refs[nextRef];
            // console.log('nextRef', nextRef);
            // console.log('nextInput', nextInput);
            if (nextInput !== undefined) {
                nextInput[0].focus();
            }

            this.lastInputFocusTime = moment();
        },
    },
};
</script>
<style lang="scss">
.voucher-card {
    font-size: 9.5pt;
    margin: 20px 20px 30px 20px;
    background-color: #fff;
    border: 2px solid transparent;
    border-radius: 8px;
    border: 1px solid #ddd;

    &.editing {
        border: 2px solid #463cb2;
    }

    .signature-field {
        display: flex;
        align-items: flex-end;
        justify-content: flex-end;
        margin-top: 16px;
        padding-right: 8px;
        font-size: 14px;
        gap: 4px;
        .signature-line {
            width: 200px;
            border-bottom: 1px solid #333;
            height: 1.4em;
        }
    }

    &.canceled {
        opacity: 0.5 !important;
    }

    .card-body {
        padding: 40px 40px 40px 40px;

        .voucher-head {
            margin-bottom: 8px;

            > .infos {
                display: flex;
                justify-content: space-between;
                line-height: 145%;
            }

            .right {
                text-align: right;
            }

            .company-name {
                text-align: center;
                font-weight: bold;
                font-size: 12pt;
            }
            .voucher-name {
                text-align: center;
                font-weight: bold;
                font-size: 12pt;
            }
            .date-display {
                text-align: center;
                font-weight: bold;
                font-size: 12pt;
                margin-bottom: 10px;
            }
        }

        .credits {
            display: flex;
            justify-content: space-between;
            margin-top: 10px;
        }
    }

    .card-footer {
        border-top: 1px solid #eee;
        padding: 12px;
        text-align: left;
        font-size: 9.5pt;
        background-color: #fff;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;

        .normal-actions {
            text-align: right;
        }

        .editing-actions {
            text-align: right;
        }
    }

    .voucher-table {
        width: 100%;
        min-height: 150px;
        font-size: 9.5pt;
        border-top: 1px solid #000;
        border-bottom: 1px solid #000;
        border-right: 1px solid #000;

        > thead {
            th {
                border-left: 1px solid #000;
                border-bottom: 1px solid #000;
                padding: 2px 7px;
                font-weight: normal;
                color: #000;

                &.setting {
                    width: 200px;
                    text-align: center;
                }
                &.content {
                    text-align: center;
                }
                &.crd-amt,
                &.deb-amt {
                    width: 110px;
                    text-align: center;
                }
                &.del {
                    width: 60px;
                }
            }
        }
        > tbody {
            td {
                // border-bottom: 1px solid #000;
                padding: 2px 7px 7px 7px;
                border-left: 1px solid #000;
                vertical-align: top;
                line-height: 140%;

                &.setting,
                &.content {
                    text-align: left;
                }
                &.crd-amt,
                &.deb-amt {
                    text-align: right;
                }
                &.add-row {
                    td {
                        border-top: 1px solid #000;
                    }
                }
                &.add-item-cell {
                    text-align: left;
                    border-top: 1px solid #000;
                    padding: 6px 7px;
                }
                &.sum-label-cell {
                    font-weight: normal;
                    text-align: right;
                }
            }
            tr.total-row {
                td {
                    height: 30px;
                    vertical-align: middle;
                    padding-top: 7px;
                    padding-bottom: 7px;
                }
            }
            tr:first-child {
                td {
                    padding-top: 7px;
                }
            }
            tr:last-child {
                td {
                    padding-bottom: 7px;
                    border-bottom: 0;
                    border-top: 1px solid #000;
                }
            }
            .money.warning {
                color: red;
            }
        }
    }
    .not-included-label {
        position: absolute;
        color: red;
        border: 1px solid red;
        padding: 0px 10px;
        border-radius: 3px;
        font-weight: bold;
        cursor: default;
    }
}
</style>
